<template>
  <BaseCard
    v-if="legalForms.length && marketLabels"
    gray
  >
    <Flex direction="column">
      <BaseDropdown
        v-model="legalForm"
        :label="$t('admin.treatwell.address_modal.dac7.legal_form_label')"
        :options="legalFormOptions"
        required
        v-test="'dac7-legal-form'"
      />

      <template
        v-for="field in fields"
        :key="field.key"
      >
        <BaseInput
          v-if="field.source === Dac7FieldSourceEnum.OwnerUser"
          v-model="ownerUser[ownerUserKey(field.key)]"
          :label="$t(`admin.treatwell.address_modal.dac7.owner_${field.key}`)"
          :required="field.required"
          v-test="`dac7-owner-${field.key}`"
        />
        <Flex
          v-else-if="field.source === Dac7FieldSourceEnum.Dac7Detail && field.requirement === Dac7FieldRequirementEnum.Optional"
          direction="column"
          :gap="0.5"
        >
          <BaseInput
            v-model="dac7Details[dac7DetailsKey(field.key)]"
            :label="fieldLabel(field.key)"
            :placeholder="field.placeholder ?? undefined"
            :disabled="optedOut[field.key]"
            :regex="optedOut[field.key] || !field.format ? undefined : new RegExp(field.format)"
            :error="errors[field.key]"
            v-test="`dac7-field-${field.key}`"
          />
          <BaseCheckbox
            :modelValue="!!optedOut[field.key]"
            @update:modelValue="setOptedOut(field.key, $event)"
            :label="$t('admin.treatwell.address_modal.dac7.no_vat_number')"
            v-test="`dac7-field-${field.key}-opt-out`"
          />
        </Flex>
        <BaseInput
          v-else-if="field.source === Dac7FieldSourceEnum.Dac7Detail && field.key !== 'legal_form'"
          v-model="dac7Details[dac7DetailsKey(field.key)]"
          :label="fieldLabel(field.key)"
          :placeholder="field.placeholder ?? undefined"
          :required="field.required"
          :regex="field.format ? new RegExp(field.format) : undefined"
          :error="errors[field.key]"
          v-test="`dac7-field-${field.key}`"
        />
      </template>
    </Flex>
  </BaseCard>
</template>

<script
  lang="ts"
  setup
>
import { useQuery } from '@vue/apollo-composable';
import { computed, reactive } from 'vue';
import type { Dac7DetailsAttributes, OwnerUserAttributes } from '@/types';
import { Dac7FieldRequirementEnum, Dac7FieldSourceEnum } from '@/types';
import { DAC7_LEGAL_FORMS, DAC7_RULES } from '../graphql';
import { dac7Labels, isDac7SupportedCountry } from './dac7-labels';

const props = defineProps<{
  country: string;
  errors?: Record<string, string>;
}>();

const errors = computed(() => props.errors ?? {});

const dac7Details = defineModel<Dac7DetailsAttributes>('dac7Details', {
  required: true,
});
const ownerUser = defineModel<OwnerUserAttributes>('ownerUser', {
  required: true,
});

const legalForm = computed({
  get: () => dac7Details.value.legalForm ?? '',
  set: (value: string) => {
    dac7Details.value.legalForm = value;
  },
});

const marketLabels = computed(() =>
  isDac7SupportedCountry(props.country) ? dac7Labels[props.country] : null,
);

const { result: legalFormsResult } = useQuery(DAC7_LEGAL_FORMS);
const legalForms = computed(() => legalFormsResult.value?.dac7LegalForms ?? []);

const legalFormOptions = computed(() =>
  legalForms.value.map((form) => ({
    value: form.key,
    label: marketLabels.value?.legalForms[form.key as never] ?? form.key,
  })),
);

const fieldLabel = (key: string) =>
  marketLabels.value?.fields[key as never] ?? key;

const { result: rulesResult } = useQuery(
  DAC7_RULES,
  () => ({ legalForm: legalForm.value }),
  { enabled: computed(() => !!legalForm.value) },
);

const fields = computed(() =>
  (rulesResult.value?.dac7Rules.fields ?? []).filter(
    (field) => field.source !== Dac7FieldSourceEnum.Company,
  ),
);

// `field.key` comes from the backend as the snake_case Ruby attribute name
// (e.g. "business_registration_number"), but the GraphQL input types use
// camelCase - these map back to the real property to write to.
const DAC7_DETAILS_KEYS: Record<string, keyof Dac7DetailsAttributes> = {
  business_registration_number: 'businessRegistrationNumber',
  tax_identification_number: 'taxIdentificationNumber',
  vat_number: 'vatNumber',
};
const OWNER_USER_KEYS: Record<string, keyof OwnerUserAttributes> = {
  first_name: 'firstName',
  last_name: 'lastName',
};

const dac7DetailsKey = (key: string) => DAC7_DETAILS_KEYS[key];
const ownerUserKey = (key: string) => OWNER_USER_KEYS[key];

// "I don't have one" for an optional field (currently only vat_number - see
// Dac7::Fields.vat_number's opt_out flag, which the API doesn't expose as a
// value to persist). There's nothing to distinguish from a blank field
// server-side, so this is UI-only: ticking it just clears the input.
const optedOut = reactive<Record<string, boolean>>({});

const setOptedOut = (key: string, value: boolean) => {
  optedOut[key] = value;
  if (value) {
    dac7Details.value[dac7DetailsKey(key)] = '';
  }
};
</script>
